/**
 * LaunchDarkly enable-* workflow flags — shared with halo-master-agent workflow_flags.py.
 * When a flag is false, hide the workflow in Trigger Workflow and Agent Studio.
 */

import axios from 'axios';
import { env } from '@/lib/env';
import { getMasterAgentApiBase } from '@/lib/reportHtml';

export const HALO_WORKFLOWS_FLAG = 'enable-halo-workflows';

export const WORKFLOW_AGENT_NUP = 'nup';
export const WORKFLOW_AGENT_TER = 'ter';
export const WORKFLOW_AGENT_PAMTER = 'pamter';
export const WORKFLOW_AGENT_PWD_RESET = 'pwd_reset';
export const WORKFLOW_AGENT_FFID = 'ffid';

export const WORKFLOW_ENABLE_FLAG_KEYS = {
  [WORKFLOW_AGENT_NUP]: 'enable-nup-workflow',
  [WORKFLOW_AGENT_TER]: 'enable-ter-workflow',
  [WORKFLOW_AGENT_PAMTER]: 'enable-pamter-workflow',
  [WORKFLOW_AGENT_PWD_RESET]: 'enable-pwd-reset-workflow',
  [WORKFLOW_AGENT_FFID]: 'enable-ffid-workflow',
};

export const WORKFLOW_GATED_AGENT_IDS = new Set(Object.keys(WORKFLOW_ENABLE_FLAG_KEYS));

/** Trigger Workflow Security sub_category → workflow agent id (null = always shown). */
export const SECURITY_SUBCATEGORY_AGENT = {
  'New User Provisioning': WORKFLOW_AGENT_NUP,
  'Firefighter ID': WORKFLOW_AGENT_FFID,
  'User Termination': WORKFLOW_AGENT_TER,
  'Password Reset': WORKFLOW_AGENT_PWD_RESET,
  'PAM Health Offboarding': WORKFLOW_AGENT_PAMTER,
};

export const ALL_WORKFLOW_ENABLE_FLAG_KEYS = [
  HALO_WORKFLOWS_FLAG,
  ...Object.values(WORKFLOW_ENABLE_FLAG_KEYS),
];

/** @typedef {{ masterEnabled: boolean, agents: Record<string, boolean> }} WorkflowEnableFlags */

/** @returns {WorkflowEnableFlags} */
export function defaultWorkflowEnableFlags() {
  return {
    masterEnabled: true,
    agents: Object.fromEntries(
      [...WORKFLOW_GATED_AGENT_IDS].map((agentId) => [agentId, true]),
    ),
  };
}

/**
 * @param {{ variation: (flag: string, defaultValue: boolean) => unknown } | null | undefined} ldClient
 * @returns {WorkflowEnableFlags}
 */
export function readWorkflowEnableFlagsFromClient(ldClient) {
  const defaults = defaultWorkflowEnableFlags();
  if (!ldClient) {
    return defaults;
  }

  const masterEnabled = ldClient.variation(HALO_WORKFLOWS_FLAG, true) !== false;
  /** @type {Record<string, boolean>} */
  const agents = {};
  for (const [agentId, flagKey] of Object.entries(WORKFLOW_ENABLE_FLAG_KEYS)) {
    agents[agentId] = ldClient.variation(flagKey, true) !== false;
  }
  return { masterEnabled, agents };
}

/**
 * Merge API payload from GET /agents/workflow-flags (preferred when present).
 * @param {WorkflowEnableFlags} current
 * @param {object | null | undefined} apiPayload
 * @returns {WorkflowEnableFlags}
 */
export function mergeWorkflowFlagsFromApi(current, apiPayload) {
  if (!apiPayload || typeof apiPayload !== 'object') {
    return current;
  }

  const base = current || defaultWorkflowEnableFlags();
  const masterEnabled = apiPayload.master_enabled !== false;
  /** @type {Record<string, boolean>} */
  const agents = { ...base.agents };
  const apiAgents = apiPayload.agents || {};
  for (const agentId of WORKFLOW_GATED_AGENT_IDS) {
    if (apiAgents[agentId] && typeof apiAgents[agentId].enabled === 'boolean') {
      agents[agentId] = apiAgents[agentId].enabled;
    }
  }
  return { masterEnabled, agents };
}

/**
 * @param {string | null | undefined} agentId
 * @param {WorkflowEnableFlags | null | undefined} flags
 */
export function isWorkflowAgentEnabled(agentId, flags) {
  const key = String(agentId || '').trim().toLowerCase();
  if (!key || !WORKFLOW_GATED_AGENT_IDS.has(key)) {
    return true;
  }
  const state = flags || defaultWorkflowEnableFlags();
  if (!state.masterEnabled) {
    return false;
  }
  return state.agents[key] !== false;
}

/**
 * @param {string[]} subcategories
 * @param {WorkflowEnableFlags | null | undefined} flags
 */
export function filterSecuritySubcategories(subcategories, flags) {
  return (subcategories || []).filter((subcategory) => {
    const agentId = SECURITY_SUBCATEGORY_AGENT[subcategory];
    if (!agentId) {
      return true;
    }
    return isWorkflowAgentEnabled(agentId, flags);
  });
}

/**
 * @param {{ agent_id?: string, tags?: string[] } | null | undefined} agent
 * @param {WorkflowEnableFlags | null | undefined} flags
 */
export function isWorkflowCatalogAgentVisible(agent, flags) {
  if (!agent) {
    return true;
  }
  const agentId = String(agent.agent_id || '').trim().toLowerCase();
  if (!agentId) {
    return true;
  }
  const tags = Array.isArray(agent.tags) ? agent.tags : [];
  const isWorkflowAgent =
    WORKFLOW_GATED_AGENT_IDS.has(agentId) || tags.includes('workflow');
  if (!isWorkflowAgent) {
    return true;
  }
  return isWorkflowAgentEnabled(agentId, flags);
}

function masterAgentWorkflowFlagsUrl() {
  const orchestrator = (env('REACT_APP_ORCHESTRATOR_URL') || '').trim().replace(/\/$/, '');
  if (orchestrator) {
    return `${orchestrator}/agents/workflow-flags`;
  }
  const base = getMasterAgentApiBase();
  if (!base) {
    return '';
  }
  return `${base.replace(/\/$/, '')}/agents/workflow-flags`;
}

/** @returns {Promise<object | null>} */
export async function fetchWorkflowFlagsFromMaster() {
  const url = masterAgentWorkflowFlagsUrl();
  if (!url) {
    return null;
  }
  try {
    const { data } = await axios.get(url, { timeout: 8000 });
    return data;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.info('[workflow-flags] master fetch skipped:', error?.message || error);
    }
    return null;
  }
}

/**
 * @param {string} pathname
 * @param {WorkflowEnableFlags | null | undefined} flags
 */
export function isPathVisibleByWorkflowFlags(_pathname, _flags) {
  // Trigger Workflow is a page. enable-halo-workflows gates individual
  // workflow types (NUP/TER/FFID/…) inside that page, not the route itself.
  return true;
}
